import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
} from "common/interfaces";
import {
    getWorkspaceResourcesSidebarWidth,
    saveWorkspaceResourcesSidebarWidth,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { WorkspaceResourceSideBarFoldersSection } from "js/react/views/SharedResources/Components/WorkspaceResourceSideBarFolders";
import { UIPaneSideBarResizer } from "js/react/components/UiComponents";
import { Divider } from "js/Components/Divider";
import WorkspaceController from "js/controllers/WorkspaceController";

import { UIPaneResultsContainer } from "../../AddSlide/Panes/Components/SearchBox";

const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

function clampWidth(width: number) {
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
}

const SideBarContainer = styled.div<{ $width: number }>`
    position: relative;
    flex-shrink: 0;
    align-self: flex-start;
    height: 100%;
    width: ${props => props.$width}px;
    min-width: ${MIN_WIDTH}px;
    max-width: ${MAX_WIDTH}px;
    box-sizing: border-box;
`;

const SideBarPanel = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: white;
    overflow: hidden;
    box-sizing: border-box;
`;

const FoldersSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const StyledUIPaneResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    padding: 15px;
    display: flex;
    flex-direction: column;

    & > *:not(${FoldersSection}) {
        flex-shrink: 0;
    }
`;

type SideBarProps = {
    resourceTypes: WorkspaceResourceType[];
    folderType?: WorkspaceResourceFolderType;
    readOnly?: boolean;
    createResourcesType?: WorkspaceResourceType;
    resourceFolders: IWorkspaceResourceFolder[];
    selectedFolderId?: string | null;
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
    onSelectFolder?: (folderId: string | null) => void;
    children?: React.ReactNode;
};

export default function SideBar({
    resourceTypes = [],
    folderType = WorkspaceResourceFolderType.SHARED,
    readOnly = false,
    createResourcesType,
    resourceFolders,
    selectedFolderId = null,
    draggedItems,
    onFolderDrop,
    onSelectFolder,
    children
}: SideBarProps) {
    const [width, setWidth] = useState(() => clampWidth(getWorkspaceResourcesSidebarWidth()));
    const sideBarRef = useRef<HTMLDivElement>(null);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

    const folders = useMemo(() => {
        return resourceFolders
            .filter(folder => (
                !folder.parentFolderId &&
                folder.folderType === WorkspaceResourceFolderType.SHARED &&
                resourceTypes.includes(folder.resourcesType)
            ))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [resourceTypes, resourceFolders]);

    const handleResize = useCallback((nextWidth: number) => {
        const clampedWidth = clampWidth(nextWidth);
        setWidth(clampedWidth);
        saveWorkspaceResourcesSidebarWidth(clampedWidth);
    }, []);

    const hideFoldersSection = folders.length === 0 && readOnly;

    return (
        <SideBarContainer ref={sideBarRef} $width={width}>
            <SideBarPanel>
                <StyledUIPaneResultsContainer>
                    {children}
                    {canUseSharedResources && !hideFoldersSection && (<>
                        <Divider margin={10} color="#e5e5e5"/>
                        <FoldersSection>
                            <WorkspaceResourceSideBarFoldersSection
                                readOnly={readOnly}
                                folders={folders}
                                selectedFolderId={selectedFolderId}
                                onSelectFolder={onSelectFolder}
                                resourceTypes={resourceTypes}
                                folderType={folderType}
                                createResourcesType={createResourcesType}
                                createFolderButtonId="shared-assets-create-folder-button"
                                draggedItems={draggedItems}
                                onFolderDrop={onFolderDrop}
                            />
                        </FoldersSection>
                    </>)}
                </StyledUIPaneResultsContainer>
            </SideBarPanel>
            <UIPaneSideBarResizer
                // @ts-expect-error UiComponents is untyped JS
                sideBarRef={sideBarRef}
                onResize={handleResize}
            />
        </SideBarContainer>
    );
}
